import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@dashai/components/ui/popover";
import { POSITIVE_CATEGORIES, NEGATIVE_CATEGORIES } from "@dashai/types/message-feedback";
import { cn } from "@dashai/lib/utils";

interface FeedbackPopoverProps {
  type: "positive" | "negative";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (categories: string[], comment?: string) => void;
  children: React.ReactNode;
  /** Extra classes for the popover box, such as a layer above the surface that opens it. */
  className?: string;
}

export function FeedbackPopover({
  type,
  open,
  onOpenChange,
  onSubmit,
  children,
  className,
}: FeedbackPopoverProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [comment, setComment] = useState("");

  const categories = type === "positive" ? POSITIVE_CATEGORIES : NEGATIVE_CATEGORIES;

  const handleCategoryToggle = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleSubmit = () => {
    onSubmit(selectedCategories, comment || undefined);
    setSelectedCategories([]);
    setComment("");
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Closing via outside-click / Escape: persist any selection instead of
      // silently discarding it — users expect picking a chip to register even
      // when they don't click the explicit Submit button.
      if (selectedCategories.length > 0 || comment.trim()) {
        onSubmit(selectedCategories, comment.trim() || undefined);
      }
      setSelectedCategories([]);
      setComment("");
    }
    onOpenChange(nextOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className={cn("w-72 p-3", className)} side="top" align="start" sideOffset={8}>
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground">
            {type === "positive" ? "What was good?" : "What went wrong?"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryToggle(category)}
                className={cn(
                  "px-2.5 py-1 text-xs rounded-full border transition-colors",
                  selectedCategories.includes(category)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                )}
              >
                {category}
              </button>
            ))}
          </div>
          <textarea
            placeholder="Anything else? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="w-full h-16 px-2.5 py-1.5 text-xs rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            onClick={handleSubmit}
            className="w-full px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Submit
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
